/**
 * Enhanced conversions voor Google Ads.
 *
 * Google matcht een aankoop aan een klik die hij anders niet ziet, door de
 * gegevens die de klant zelf invulde te vergelijken met ingelogde
 * Google-gebruikers. Vooral bij iDEAL en op mobiel, waar het cookie vaak
 * ontbreekt, scheelt dat merkbaar.
 *
 * We hashen in de browser met SHA-256, zodat er nooit leesbare klantgegevens
 * de pagina verlaten. Wat we niet met zekerheid kunnen normaliseren laten we
 * weg: een verkeerd genormaliseerd telefoonnummer matcht nooit en maakt de
 * hash alleen maar misleidend.
 */

const DIAL_CODES: Record<string, string> = {
  NL: '31',
  BE: '32',
  DE: '49',
  FR: '33',
  LU: '352',
  AT: '43',
  ES: '34',
  IT: '39',
  GB: '44',
  DK: '45',
  SE: '46',
  PL: '48',
};

/**
 * E-mail zoals Google hem wil hashen: lowercase, zonder spaties, en bij
 * gmail.com/googlemail.com zonder punten voor de @. Gedeeld door browser
 * (gtag user_data) en server (GA4 Measurement Protocol), zodat beide kanten
 * voor dezelfde klant dezelfde hash opleveren.
 */
export function normalizeEmailForGoogle(value: unknown) {
  const email = String(value || '').trim().toLowerCase().replace(/\s+/g, '');
  const at = email.lastIndexOf('@');
  if (at <= 0 || at === email.length - 1) return undefined;
  let local = email.slice(0, at);
  const domain = email.slice(at + 1);
  if (domain === 'gmail.com' || domain === 'googlemail.com') local = local.replace(/\./g, '');
  return local ? `${local}@${domain}` : undefined;
}

/** Voor- en achternaam: zonder cijfers en leestekens, lowercase, getrimd. */
export function normalizeNameForGoogle(value: unknown) {
  const text = String(value || '')
    .normalize('NFC')
    .replace(/[^\p{L}\s]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return text || undefined;
}

async function sha256(value: string | undefined | null) {
  const input = String(value || '').trim().toLowerCase();
  if (!input) return undefined;
  if (!globalThis.crypto?.subtle) return undefined;
  const bytes = new TextEncoder().encode(input);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/** Zet een nummer om naar E.164, of geeft niets terug als dat niet zeker kan. */
export function toE164(raw: unknown, country: unknown) {
  const cleaned = String(raw || '').replace(/[^\d+]/g, '');
  if (!cleaned) return undefined;
  if (cleaned.startsWith('+')) return cleaned.length > 8 ? cleaned : undefined;
  if (cleaned.startsWith('00')) {
    const rest = cleaned.slice(2);
    return rest.length > 8 ? `+${rest}` : undefined;
  }
  const dialCode = DIAL_CODES[String(country || '').trim().toUpperCase()];
  if (!dialCode) return undefined;
  const local = cleaned.replace(/^0+/, '');
  if (local.length < 8) return undefined;
  return `+${dialCode}${local}`;
}

export type EnhancedConversionInput = {
  email?: unknown;
  phone?: unknown;
  firstName?: unknown;
  lastName?: unknown;
  city?: unknown;
  zip?: unknown;
  country?: unknown;
};

/**
 * Bouwt het `user_data`-object voor `gtag('set', 'user_data', ...)`.
 * Geeft null terug als er niets bruikbaars is, zodat de aanroeper de
 * conversie gewoon zonder klantgegevens kan versturen.
 */
export async function buildEnhancedUserData(input: EnhancedConversionInput) {
  const [email, phone, firstName, lastName] = await Promise.all([
    sha256(normalizeEmailForGoogle(input.email)),
    sha256(toE164(input.phone, input.country)),
    sha256(normalizeNameForGoogle(input.firstName)),
    sha256(normalizeNameForGoogle(input.lastName)),
  ]);

  const address: Record<string, string> = {};
  if (firstName) address.sha256_first_name = firstName;
  if (lastName) address.sha256_last_name = lastName;
  // Plaats, postcode en land gaan onversleuteld mee; zo wil Google ze hebben.
  const city = String(input.city || '').trim();
  const zip = String(input.zip || '').trim();
  const country = String(input.country || '').trim().toUpperCase();
  if (city) address.city = city;
  if (zip) address.postal_code = zip;
  if (country) address.country = country;

  const userData: Record<string, unknown> = {};
  if (email) userData.sha256_email_address = email;
  if (phone) userData.sha256_phone_number = phone;
  if (Object.keys(address).length > 0) userData.address = address;

  return Object.keys(userData).length > 0 ? userData : null;
}

/** Velden met klantgegevens die nooit als GA4-eventparameter mee mogen. */
export const CUSTOMER_DATA_KEYS = [
  'email', 'phone', 'firstName', 'lastName', 'first_name', 'last_name',
  'address', 'addressLine1', 'addressLine2', 'street', 'postalCode',
  'zip', 'city', 'externalId', 'customer', 'customerData', 'user_data', 'userData',
] as const;

export function hasCustomerIdentifiers(payload: Record<string, unknown>) {
  return Boolean(normalizeEmailForGoogle(payload.email) || String(payload.phone || '').trim());
}

/** Kopie van het payload zonder klantgegevens, voor GA4-eventparameters. */
export function withoutCustomerData<T extends Record<string, unknown>>(payload: T) {
  const copy: Record<string, unknown> = { ...payload };
  CUSTOMER_DATA_KEYS.forEach((key) => delete copy[key]);
  return copy as Partial<T>;
}
